import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUpRight, Banknote, Pencil, Plus, Undo2 } from 'lucide-react';
import { categoryLabel } from '@/features/quick-add/quick-add';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useBaseCurrency } from '@/hooks/useOrgSettings';
import { useIsPhone } from '@/hooks/useIsPhone';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { usePagedList } from '@/hooks/usePagedList';
import { ListFooter } from '@/features/common/ListFooter';
import { PageHeader } from '@/app/DashboardLayout';
import { CurrencySelect } from '@/features/common/CurrencyRate';
import { AdvancedFilter, useAdvancedFilter } from '@/features/common/AdvancedFilter';
import { activeOnly } from '@/features/common/Drafts';
import {
  Badge, Button, Card, CardFacts, CardLine, CardRow, Cards, EmptyState, ErrorState, Input, Money, SkeletonTable,
  TBody, TD, TH, THead, TR, Table,
  SelectField, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader,
} from '@/features/common/ui';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/dates';
import { RefundOfCostPicker } from '@/features/expenses/RefundSheets';
import { SearchChip, useSearchTerm } from '@/features/search/SearchChip';
import {
  AccountTypeSelect,
  InvestmentTermsFields,
  TermsLine,
  TransferChoice,
  TYPE_LABEL,
  emptyTerms,
  isInvestment,
  termsBody,
  termsFrom,
  type AccountType,
} from './investments';

interface Account {
  id: string;
  name: string;
  type: AccountType;
  /** An FD, RD, fund or chit: shown apart, and never counted as cash. */
  isInvestment?: boolean;
  principalAmount?: string | null;
  interestRate?: string | null;
  startDate?: string | null;
  maturityDate?: string | null;
  autoRenew?: boolean;
  bankName: string | null;
  accountNumberLast4: string | null;
  currency: string;
  balance: string;
  isPrimary: boolean;
  /** What was in it when tracking began here, and when. Editable; the balance is derived from it. */
  openingBalance: string;
  openingBalanceDate: string | null;
}

interface Transaction {
  id: string;
  accountId: string;
  accountName: string;
  date: string;
  direction: 'deposit' | 'withdrawal';
  amount: string;
  description: string | null;
  payee: string | null;
  status: 'uncategorised' | 'categorised' | 'matched' | 'excluded';
  linkType: string | null;
  /** The record Fint wrote this row for, when it wrote it - a payment, an expense. Null on a row a person or a statement put here. */
  linkId?: string | null;
  incomeCategoryId?: string | null;
  currency: string;
  /**
   * Who paid, and for what: a direct receipt's answer to the two things an
   * invoice would have said. Set from the deposit sheet here, or by
   * confirming a change the AI agent proposed in the Inbox. Optional on the
   * wire while the API lands them; a row without them reads as before.
   */
  partyId?: string | null;
  itemId?: string | null;
  partyName?: string | null;
  itemName?: string | null;
}

/**
 * A deposit that is income in its own right - not the cash side of an
 * invoice payment (which already knows its customer), not a transfer leg,
 * not excluded. The same rule the server applies before it lets a row be
 * told what kind of income it was; here it decides whether to offer Edit,
 * so the pencil never opens a sheet whose every save would be refused.
 */
function isDirectDeposit(t: Transaction): boolean {
  return (
    t.direction === 'deposit' &&
    t.status !== 'excluded' &&
    !t.linkId &&
    (t.linkType === null || t.linkType === 'other_income')
  );
}

/**
 * Money a partner or owner put in, filed as such: in the balance, in no
 * income figure. Still a person's own label on a row they entered (no
 * `linkId`), so the sheet opens on it - to say it was income after all.
 */
function isCapital(t: Transaction): boolean {
  return t.direction === 'deposit' && t.status !== 'excluded' && !t.linkId && t.linkType === 'owner_contribution';
}

/** A deposit the sheet may open on: income to classify, or capital to un-file. */
const isEditableDeposit = (t: Transaction): boolean => isDirectDeposit(t) || isCapital(t);

/** One leg of a transfer between the book's own accounts - undone as a pair. */
const isTransferLeg = (t: Transaction): boolean => t.linkType === 'transfer' && !!t.linkId;

/**
 * Money out that a person or a statement put here - not the cash side of an
 * expense, a payment or a transfer, which follow their records. The sheet
 * opens on it to say it was a partner drawing, or to correct the line.
 */
function isDirectWithdrawal(t: Transaction): boolean {
  return (
    t.direction === 'withdrawal' &&
    t.status !== 'excluded' &&
    !t.linkId &&
    (t.linkType === null || t.linkType === 'owner_drawing' || t.linkType === 'other_expense')
  );
}

/** "Paid by PKPP Tech · For Consulting" - under the description, when the row knows either. */
function Attribution({ transaction }: { transaction: Transaction }) {
  const parts = [
    transaction.partyName ? `Paid by ${transaction.partyName}` : null,
    transaction.itemName ? `For ${transaction.itemName}` : null,
  ].filter((p): p is string => !!p);
  if (!parts.length) return null;
  return (
    <div className="text-xs text-muted-foreground" data-testid="transaction-attribution">
      {parts.join(' · ')}
    </div>
  );
}

function DirectionIcon({ direction }: { direction: Transaction['direction'] }) {
  return direction === 'deposit' ? (
    <ArrowDownLeft className="size-4 shrink-0 text-success" aria-label="Money in" />
  ) : (
    <ArrowUpRight className="size-4 shrink-0 text-muted-foreground" aria-label="Money out" />
  );
}

function TransactionStatus({ transaction }: { transaction: Transaction }) {
  if (transaction.status === 'uncategorised') return <Badge variant="warning">Needs review</Badge>;
  if (transaction.status === 'excluded') return <Badge>Excluded</Badge>;
  // Capital's own words: "owner contribution" is the column, not what a
  // partner calls the money they put into an LLP.
  if (transaction.linkType === 'owner_contribution') return <Badge variant="neutral">Capital introduced</Badge>;
  // Money back on a cost (R-04): in the balance, in no income figure, netted
  // against the charge it came back on.
  if (transaction.linkType === 'expense_refund') return <Badge variant="neutral">Refund</Badge>;
  // A partner taking money out: in the balance, in no expense figure.
  if (transaction.linkType === 'owner_drawing') return <Badge variant="neutral">Drawing</Badge>;
  return <Badge variant="neutral">{transaction.linkType?.replace(/_/g, ' ') ?? 'Categorised'}</Badge>;
}

function TransactionAmount({ transaction }: { transaction: Transaction }) {
  return (
    <Money
      // Direction is a separate column, so the sign is applied here rather
      // than being baked into the stored amount.
      value={transaction.direction === 'withdrawal' ? `-${transaction.amount}` : transaction.amount}
      currency={transaction.currency}
      colorNegative
    />
  );
}

export function BankingPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [accountFormOpen, setAccountFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [editingDeposit, setEditingDeposit] = useState<Transaction | null>(null);
  const [editingWithdrawal, setEditingWithdrawal] = useState<Transaction | null>(null);
  const isPhone = useIsPhone();
  // `banking.manage`, as classifying a row is: a decision about a bank row,
  // made by whoever reconciles. The server holds the same line.
  const canEditDeposit = perms.can('banking', 'manage');

  const accounts = useQuery({
    queryKey: ['banking', org?.id, 'accounts'],
    queryFn: () => api.get<{ items: Account[] }>('/banking/accounts'),
    enabled: !!org?.id,
  });

  // The condition builder, in the URL: "uncategorised deposits over ₹50,000
  // since April" is the reconciliation question, and a link.
  const advanced = useAdvancedFilter();
  const search = useSearchTerm();
  const transactions = usePagedList<Transaction>({
    key: ['banking', org?.id, 'transactions', selectedAccount, advanced.key, search.q],
    path: (cursor) =>
      `/banking/transactions${qs({ accountId: selectedAccount ?? undefined, cursor, q: search.q || undefined, ...advanced.params })}`,
    enabled: !!org?.id,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['banking', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard', org?.id] });
    // Payments lists direct receipts by who paid; a deposit edited here is one.
    void queryClient.invalidateQueries({ queryKey: ['payments-direct', org?.id] });
    // A deposit filed as a refund writes a negative row into the charge's
    // category, so the expense list and its totals move too.
    void queryClient.invalidateQueries({ queryKey: ['expenses', org?.id] });
  };

  // A transfer recorded to the wrong account is taken back as a pair: the leg
  // Fint wrote goes, a statement line returns to Needs review.
  const undoTransfer = useMutation({
    mutationFn: (transactionId: string) => api.delete(`/banking/transfers/${transactionId}`),
    onSuccess: refresh,
  });
  const confirmUndo = (transaction: Transaction) => {
    if (window.confirm('Undo this transfer? Both sides go back: a line Fint wrote is removed, and a statement line returns to Needs review.')) {
      undoTransfer.mutate(transaction.id);
    }
  };
  const cashAccounts = accounts.data?.items.filter((a) => !a.isInvestment) ?? [];
  const investmentAccounts = accounts.data?.items.filter((a) => a.isInvestment) ?? [];

  const renderAccount = (account: Account) => (
    // A wrapper, so the Edit control is a SIBLING of the card's
    // button rather than a child: a button inside a button is
    // invalid markup, and the inner label leaked into the card's
    // accessible name.
    <div key={account.id} className="relative">
    <button
      type="button"
      onClick={() => setSelectedAccount(selectedAccount === account.id ? null : account.id)}
      aria-pressed={selectedAccount === account.id}
      className={cn(
        'w-full rounded-lg border bg-card p-4 text-left transition-colors',
        // The card IS the button, so its edge is a control boundary
        // and needs 3:1 - the divider colour gave 1.23:1 on white.
        selectedAccount === account.id ? 'border-primary' : 'border-input hover:border-primary',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{account.name}</p>
          <p className="text-xs text-muted-foreground">
            {TYPE_LABEL[account.type]}
            {account.accountNumberLast4 ? ` ···· ${account.accountNumberLast4}` : ''}
          </p>
          {account.isInvestment ? <TermsLine account={account} /> : null}
        </div>
        <span className={cn('flex shrink-0 items-center gap-1', perms.can('banking', 'edit') && 'pr-8 touch:pr-11')}>
          {account.isPrimary ? <Badge variant="info">Primary</Badge> : null}
        </span>
      </div>
      <p className="mt-3 text-xl font-semibold">
        {/* Derived from the transactions underneath, never a stored
            column - so it cannot silently disagree with them. */}
        <Money value={account.balance} currency={account.currency} colorNegative />
      </p>
    </button>
    {perms.can('banking', 'edit') ? (
      <button
        type="button"
        aria-label={`Edit ${account.name}`}
        onClick={() => setEditingAccount(account)}
        className="absolute right-3 top-3 flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent touch:size-11"
        data-testid={`edit-account-${account.id}`}
      >
        <Pencil className="size-4" aria-hidden />
      </button>
    ) : null}
    </div>
  );

  return (
    <>
      <PageHeader
        title="Banking"
        subtitle="Every account, and the money moving through it."
        actions={
          perms.can('banking', 'create') ? (
            <Button size="sm" onClick={() => setAccountFormOpen(true)}>
              <Plus className="size-4" aria-hidden />
              Add account
            </Button>
          ) : null
        }
      />

      <div className="space-y-4 px-5 pb-8">
        <SearchChip q={search.q} onClear={search.clear} />
        {accounts.isError ? (
          <Card>
            <ErrorState error={accounts.error} onRetry={() => accounts.refetch()} />
          </Card>
        ) : accounts.isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="skeleton h-24 rounded-lg" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cashAccounts.map((account) => renderAccount(account))}
            </div>
            {investmentAccounts.length ? (
              <section aria-labelledby="investments-heading" className="space-y-2" data-testid="investment-accounts">
                <h2 id="investments-heading" className="text-sm font-semibold">
                  Savings and investments
                  <span className="ml-2 font-normal text-muted-foreground">not counted as cash</span>
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {investmentAccounts.map((account) => renderAccount(account))}
                </div>
              </section>
            ) : null}
          </>
        )}

        {undoTransfer.error instanceof ApiError ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {undoTransfer.error.message}
          </p>
        ) : null}

        <Card>
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h2 className="text-sm font-semibold">
              {selectedAccount
                ? `Transactions · ${accounts.data?.items.find((a) => a.id === selectedAccount)?.name ?? ''}`
                : 'All transactions'}
            </h2>
            {selectedAccount ? (
              <Button variant="ghost" size="sm" onClick={() => setSelectedAccount(null)}>
                Show all
              </Button>
            ) : null}
          </div>
          <div className="border-b border-border px-5 py-3">
            <AdvancedFilter entityType="transaction" value={advanced.value} onChange={advanced.set} />
          </div>

          {transactions.isError ? (
            <ErrorState error={transactions.error} onRetry={() => transactions.refetch()} />
          ) : transactions.isLoading ? (
            <SkeletonTable rows={5} cols={4} />
          ) : transactions.items.length ? (
            <>
            {/*
              Cards on a phone, as every other list: five columns in 320px put
              the Amount - the one figure the row is for - past the right edge
              behind a scroll nobody finds. See RowCards for why the row is
              written twice rather than squeezed once.
            */}
            {isPhone ? (
              <Cards>
                {transactions.items.map((transaction) => (
                  <CardRow
                    key={transaction.id}
                    className={transaction.status === 'excluded' ? 'opacity-50' : undefined}
                    data-testid="transaction-row"
                  >
                    <CardLine
                      title={
                        <span className="flex items-center gap-2">
                          <DirectionIcon direction={transaction.direction} />
                          <span className="truncate">{transaction.description ?? transaction.payee ?? '—'}</span>
                        </span>
                      }
                      meta={transaction.accountName}
                      value={<TransactionAmount transaction={transaction} />}
                    />
                    <Attribution transaction={transaction} />
                    <CardFacts>
                      <span>{formatDate(transaction.date)}</span>
                      <TransactionStatus transaction={transaction} />
                      {canEditDeposit && isEditableDeposit(transaction) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-my-2 ml-auto"
                          onClick={() => setEditingDeposit(transaction)}
                          aria-label={`Edit deposit ${transaction.description ?? transaction.payee ?? ''}`.trim()}
                        >
                          <Pencil className="size-4" aria-hidden />
                          Edit
                        </Button>
                      ) : canEditDeposit && isDirectWithdrawal(transaction) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-my-2 ml-auto"
                          onClick={() => setEditingWithdrawal(transaction)}
                          aria-label={`Edit withdrawal ${transaction.description ?? transaction.payee ?? ''}`.trim()}
                        >
                          <Pencil className="size-4" aria-hidden />
                          Edit
                        </Button>
                      ) : canEditDeposit && isTransferLeg(transaction) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-my-2 ml-auto"
                          loading={undoTransfer.isPending && undoTransfer.variables === transaction.id}
                          onClick={() => confirmUndo(transaction)}
                          aria-label={`Undo transfer ${transaction.description ?? ''}`.trim()}
                        >
                          <Undo2 className="size-4" aria-hidden />
                          Undo
                        </Button>
                      ) : null}
                    </CardFacts>
                  </CardRow>
                ))}
              </Cards>
            ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Date</TH>
                  <TH>Description</TH>
                  <TH>Account</TH>
                  <TH>Status</TH>
                  <TH numeric>Amount</TH>
                  {canEditDeposit ? (
                    <TH className="w-12">
                      <span className="sr-only">Actions</span>
                    </TH>
                  ) : null}
                </TR>
              </THead>
              <TBody>
                {transactions.items.map((transaction) => (
                  <TR
                    key={transaction.id}
                    className={transaction.status === 'excluded' ? 'opacity-50' : undefined}
                    data-testid="transaction-row"
                  >
                    <TD className="whitespace-nowrap text-muted-foreground">{formatDate(transaction.date)}</TD>
                    <TD>
                      <div className="flex items-center gap-2">
                        <DirectionIcon direction={transaction.direction} />
                        <span>{transaction.description ?? transaction.payee ?? '—'}</span>
                      </div>
                      <Attribution transaction={transaction} />
                    </TD>
                    <TD className="text-muted-foreground">{transaction.accountName}</TD>
                    <TD>
                      <TransactionStatus transaction={transaction} />
                    </TD>
                    <TD numeric>
                      <TransactionAmount transaction={transaction} />
                    </TD>
                    {canEditDeposit ? (
                      <TD className="text-right">
                        {isEditableDeposit(transaction) ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingDeposit(transaction)}
                            aria-label={`Edit deposit ${transaction.description ?? transaction.payee ?? ''}`.trim()}
                          >
                            <Pencil className="size-4" aria-hidden />
                          </Button>
                        ) : isDirectWithdrawal(transaction) ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingWithdrawal(transaction)}
                            aria-label={`Edit withdrawal ${transaction.description ?? transaction.payee ?? ''}`.trim()}
                          >
                            <Pencil className="size-4" aria-hidden />
                          </Button>
                        ) : isTransferLeg(transaction) ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            loading={undoTransfer.isPending && undoTransfer.variables === transaction.id}
                            onClick={() => confirmUndo(transaction)}
                            aria-label={`Undo transfer ${transaction.description ?? ''}`.trim()}
                            data-testid="undo-transfer"
                          >
                            <Undo2 className="size-4" aria-hidden />
                          </Button>
                        ) : null}
                      </TD>
                    ) : null}
                  </TR>
                ))}
              </TBody>
            </Table>
            )}
            <ListFooter query={transactions} noun="transactions" />
            </>
          ) : (
            <EmptyState
              icon={Banknote}
              title={advanced.active ? 'Nothing matches that filter' : 'No transactions yet'}
              description={advanced.active ? undefined : 'Recording an expense or a payment puts it here automatically.'}
            />
          )}
        </Card>
      </div>

      {accountFormOpen ? <AccountForm onClose={() => setAccountFormOpen(false)} onCreated={() => { setAccountFormOpen(false); refresh(); }} /> : null}
      {editingAccount ? (
        <EditAccountForm
          account={editingAccount}
          onClose={() => setEditingAccount(null)}
          onSaved={() => {
            setEditingAccount(null);
            refresh();
          }}
        />
      ) : null}
      {editingDeposit ? (
        <DepositSheet
          transaction={editingDeposit}
          accounts={accounts.data?.items ?? []}
          onClose={() => setEditingDeposit(null)}
          onSaved={() => {
            setEditingDeposit(null);
            refresh();
          }}
        />
      ) : null}
      {editingWithdrawal ? (
        <WithdrawalSheet
          transaction={editingWithdrawal}
          accounts={accounts.data?.items ?? []}
          onClose={() => setEditingWithdrawal(null)}
          onSaved={() => {
            setEditingWithdrawal(null);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

interface PartyOption {
  id: string;
  displayName: string;
  status?: string;
}
interface ItemOption {
  id: string;
  name: string;
  status?: string;
}
interface CategoryOption {
  id: string;
  name: string;
  emoji?: string | null;
}

/**
 * A recorded deposit: what kind of income it was, who paid, and for what.
 *
 * ── WHY THIS SHEET EXISTS ──
 * Payments said "change or exclude one on Banking" and Banking had nowhere
 * to change one: the income category could be set by the API, by Quick add
 * and by the Inbox's deposit sheet, and not on the page that lists the
 * deposits. And "Paid by" and "For" are the two things a direct receipt
 * cannot say at all - an invoice names its customer and its lines; ₹50,000
 * by NEFT names neither - so Payments listed a lakh of consulting fees as
 * "NEFT Cr PKPP". A person can now say it here; the AI agent proposes it
 * as a change in the Inbox and it lands through the same route on confirm.
 *
 * One PUT to the attribution route, carrying only the keys that changed -
 * the same body a change proposal carries, so a person setting it by hand
 * and the Inbox applying a proposal are held to one contract. `null`
 * clears a field; an absent key leaves it. Nothing here moves a figure.
 */
function DepositSheet({
  transaction,
  accounts,
  onClose,
  onSaved,
}: {
  transaction: Transaction;
  accounts: Account[];
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const [form, setForm] = useState({
    incomeCategoryId: transaction.incomeCategoryId ?? '',
    partyId: transaction.partyId ?? '',
    itemId: transaction.itemId ?? '',
  });

  const categories = useQuery({
    queryKey: ['income-categories', org?.id],
    queryFn: () => api.get<{ items: CategoryOption[] }>('/income-categories'),
    enabled: !!org?.id,
  });
  // Active rows only: a draft the AI agent proposed is in no picker until
  // somebody confirms it, and a cached list from before is filtered too.
  const customers = useQuery({
    queryKey: ['customers', org?.id, 'picker'],
    queryFn: () => api.get<{ items: PartyOption[] }>('/customers?status=active&limit=200').then(activeOnly),
    enabled: !!org?.id,
  });
  const items = useQuery({
    queryKey: ['items', org?.id, 'picker'],
    queryFn: () => api.get<{ items: ItemOption[] }>('/items?status=active&limit=200').then(activeOnly),
    enabled: !!org?.id,
  });
  const customerOptions = customers.data?.items ?? [];
  const itemOptions = items.data?.items ?? [];

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, string | null> = {};
      if (form.incomeCategoryId !== (transaction.incomeCategoryId ?? '')) body.incomeCategoryId = form.incomeCategoryId || null;
      if (form.partyId !== (transaction.partyId ?? '')) body.partyId = form.partyId || null;
      if (form.itemId !== (transaction.itemId ?? '')) body.itemId = form.itemId || null;
      // Nothing changed: nothing to send. The schema refuses an empty body,
      // and "Propose at least one change" is not a sentence for a person
      // who pressed Save on an untouched sheet.
      if (Object.keys(body).length === 0) return;
      await api.put(`/banking/transactions/${transaction.id}/attribution`, body);
    },
    onSuccess: onSaved,
  });
  /**
   * Capital or income: a change of what the money IS, not of who paid it.
   *
   * Through `categorise`, which takes the income category off the row when it
   * becomes capital - a category left on a row the income report skips is money
   * gone from both the figure and the disclosure. Going back to income files it
   * as `other_income` with no category yet, which the review then asks about.
   */
  const capital = isCapital(transaction);
  /**
   * Or money back on a cost - a vendor refund, a reversed duplicate charge.
   * Not income either, and not capital: it belongs against the charge it
   * reverses, so the category nets. The picker adopts THIS line as the
   * refund's cash side rather than writing a second one, which is what keeps
   * the balance moving once.
   */
  const [refunding, setRefunding] = useState(false);
  const refile = useMutation({
    mutationFn: (linkType: 'owner_contribution' | 'other_income') =>
      api.post(`/banking/transactions/${transaction.id}/categorise`, { linkType }),
    onSuccess: onSaved,
  });
  const error =
    save.error instanceof ApiError ? save.error : refile.error instanceof ApiError ? refile.error : undefined;

  return (
    <Sheet label="Edit deposit" onClose={onClose}>
      <SheetHeader title="Edit deposit" onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          <div className="rounded-md bg-muted px-3 py-2 text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate">{transaction.description ?? transaction.payee ?? 'Deposit'}</span>
              <span className="tabular shrink-0 font-medium">
                <Money value={transaction.amount} currency={transaction.currency} />
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {formatDate(transaction.date)} · {transaction.accountName}
            </div>
          </div>

          {refunding ? (
            <RefundOfCostPicker
              credit={{
                id: transaction.id,
                accountId: transaction.accountId,
                date: transaction.date,
                amount: transaction.amount,
                currency: transaction.currency,
                description: transaction.description,
                payee: transaction.payee,
              }}
              onDone={onSaved}
              onCancel={() => setRefunding(false)}
            />
          ) : capital ? (
            <div className="space-y-2 rounded-md border border-border p-3 text-sm" data-testid="deposit-capital">
              <p className="font-medium">Filed as capital introduced</p>
              <p className="text-xs text-muted-foreground">
                Money a partner or owner put into the business. It is in the balance and in no income figure, so it
                has no income category.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={refile.isPending}
                onClick={() => refile.mutate('other_income')}
                data-testid="deposit-was-income"
              >
                It was income after all
              </Button>
            </div>
          ) : (
            <>
              <SelectField
                id="deposit-income-category"
                label="Income category"
                value={form.incomeCategoryId}
                onChange={(incomeCategoryId) => setForm((f) => ({ ...f, incomeCategoryId }))}
                error={error?.fieldError('incomeCategoryId')}
              >
                <option value="">Not sure</option>
                {(categories.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {categoryLabel(c.name, c.emoji)}
                  </option>
                ))}
              </SelectField>
              <SelectField
                id="deposit-party"
                label="Paid by"
                value={form.partyId}
                onChange={(partyId) => setForm((f) => ({ ...f, partyId }))}
                error={error?.fieldError('partyId')}
              >
                <option value="">Nobody in particular</option>
                {customerOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </SelectField>
              <SelectField
                id="deposit-item"
                label="For"
                value={form.itemId}
                onChange={(itemId) => setForm((f) => ({ ...f, itemId }))}
                error={error?.fieldError('itemId')}
              >
                <option value="">Nothing in particular</option>
                {itemOptions.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </SelectField>
              <p className="text-xs text-muted-foreground">
                Who paid and for what - the two things an invoice would have said. Neither changes the amount or the balance.
              </p>
              <div className="rounded-md border border-dashed border-border p-3 text-sm">
                <p className="text-xs text-muted-foreground">
                  Money a partner or owner put in - an LLP contribution, topping up the account - is capital, not income.
                  Filed as income, it overstates the year&rsquo;s income by the whole amount.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  loading={refile.isPending}
                  onClick={() => refile.mutate('owner_contribution')}
                  data-testid="deposit-is-capital"
                >
                  This was capital, not income
                </Button>
              </div>
              <div className="rounded-md border border-dashed border-border p-3 text-sm">
                <p className="text-xs text-muted-foreground">
                  Money back on something you paid for - a vendor refund, a duplicate charge reversed - is not income
                  either. Filed as income it overstates income AND leaves the cost it reverses in the spend.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setRefunding(true)}
                  data-testid="deposit-is-refund"
                >
                  This was a refund of a cost
                </Button>
              </div>
              <TransferChoice transaction={transaction} accounts={accounts} onSaved={onSaved} />
            </>
          )}

          {error && !error.errors?.length ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
          {refunding ? null : <LineCorrection transaction={transaction} accounts={accounts} onSaved={onSaved} />}
        </SheetBody>
        <SheetFooter>
          {capital || refunding ? null : <Button type="submit" loading={save.isPending}>Save changes</Button>}
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

/**
 * Correct the line itself - date, amount, description, account - on a line a
 * person or a statement put here (Sai: "editable everything by an admin ...
 * with audit trail").
 *
 * Folded away by default: classifying a line is the everyday act on this
 * sheet, and correcting what the bank said is the rare one. A reason is
 * required, because the line is what a statement said and the trail has to
 * say why Fint now says something else. The server refuses a line Fint wrote
 * (it follows its record) and checks the period on both dates.
 */
function LineCorrection({
  transaction,
  accounts,
  onSaved,
}: {
  transaction: Transaction;
  accounts: Account[];
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: transaction.date,
    amount: transaction.amount,
    description: transaction.description ?? '',
    accountId: transaction.accountId,
    reason: '',
  });
  const sameCurrency = accounts.filter((a) => a.currency === transaction.currency);
  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, string> = { reason: form.reason.trim() };
      if (form.date !== transaction.date) body.date = form.date;
      if (form.amount !== transaction.amount) body.amount = form.amount;
      if (form.description !== (transaction.description ?? '')) body.description = form.description;
      if (form.accountId !== transaction.accountId) body.accountId = form.accountId;
      return api.patch(`/banking/transactions/${transaction.id}`, body);
    },
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;

  if (!open) {
    return (
      <button
        type="button"
        className="text-left text-xs text-muted-foreground underline-offset-2 hover:underline"
        onClick={() => setOpen(true)}
        data-testid="line-correct-open"
        data-tap-exempt="text button under the sheet's form; the hit area is drawn around it"
      >
        The bank line itself is wrong - correct its date, amount or account
      </button>
    );
  }
  return (
    <div className="space-y-3 rounded-md border border-border p-3" data-testid="line-correct">
      <p className="text-sm font-medium">Correct the line</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} error={error?.fieldError('date')} data-testid="line-date" />
        <Input label="Amount" inputMode="decimal" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} error={error?.fieldError('amount')} data-testid="line-amount" />
      </div>
      <Input label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} error={error?.fieldError('description')} />
      {sameCurrency.length > 1 ? (
        <SelectField id={`line-account-${transaction.id}`} label="Account" value={form.accountId} onChange={(accountId) => setForm((f) => ({ ...f, accountId }))} error={error?.fieldError('accountId')}>
          {sameCurrency.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </SelectField>
      ) : null}
      {/* Not `required`: this sits inside the deposit sheet's own form, and a
          browser-required field here would block that form's Save too. The
          button below is what waits for a reason. */}
      <Input
        label="Why"
        value={form.reason}
        onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
        placeholder="The statement shows ₹12,890 on 24 Jul"
        error={error?.fieldError('reason')}
        data-testid="line-reason"
      />
      {error && !error.errors?.length ? (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="button" size="sm" loading={save.isPending} disabled={form.reason.trim().length < 3} onClick={() => save.mutate()} data-testid="line-save">
          Save correction
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Never mind
        </Button>
      </div>
    </div>
  );
}

/**
 * Money out that a person or a statement put on the account.
 *
 * ── DRAWINGS, AND WHY THEY WERE OFF THE SCREEN UNTIL SAI ASKED ──
 * A drawing is a partner or owner taking money OUT of the business - the
 * mirror of capital introduced. It is in the balance and in no expense
 * figure, because it is not a cost of the business. That is exactly what
 * makes it dangerous on screen: partner REMUNERATION - the salary an LLP pays
 * a working partner - IS an expense, deductible, filed under Owner's salary,
 * and it looks identical on a bank statement. Filed as a drawing, it would
 * vanish from the P&L and overstate profit by the whole salary. So the button
 * says which one it is for, and the sheet says the difference.
 */
function WithdrawalSheet({
  transaction,
  accounts,
  onClose,
  onSaved,
}: {
  transaction: Transaction;
  accounts: Account[];
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const drawing = transaction.linkType === 'owner_drawing';
  const refile = useMutation({
    mutationFn: () => api.post(`/banking/transactions/${transaction.id}/categorise`, { linkType: 'owner_drawing' }),
    onSuccess: onSaved,
  });
  const error = refile.error instanceof ApiError ? refile.error : undefined;

  return (
    <Sheet label="Edit withdrawal" onClose={onClose}>
      <SheetHeader title="Edit withdrawal" onClose={onClose} />
      <SheetBody>
        <div className="rounded-md bg-muted px-3 py-2 text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate">{transaction.description ?? transaction.payee ?? 'Withdrawal'}</span>
            <span className="tabular shrink-0 font-medium">
              <Money value={transaction.amount} currency={transaction.currency} />
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            {formatDate(transaction.date)} · {transaction.accountName}
          </div>
        </div>

        {drawing ? (
          <div className="space-y-1 rounded-md border border-border p-3 text-sm" data-testid="withdrawal-drawing">
            <p className="font-medium">Filed as a partner drawing</p>
            <p className="text-xs text-muted-foreground">
              Money a partner or owner took out. It is in the balance and in no expense figure.
            </p>
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-border p-3 text-sm">
            <p className="text-xs text-muted-foreground">
              Money a partner or owner took OUT of the business is a drawing - not an expense, and in no P&amp;L
              figure. A partner&rsquo;s monthly remuneration is different: that IS an expense, filed under
              Owner&rsquo;s salary. Only use this for money taken out, not for pay.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              loading={refile.isPending}
              onClick={() => refile.mutate()}
              data-testid="withdrawal-is-drawing"
            >
              This was a partner drawing, not an expense
            </Button>
          </div>
        )}
        <TransferChoice transaction={transaction} accounts={accounts} onSaved={onSaved} />

        {error ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error.message}
          </p>
        ) : null}
        <LineCorrection transaction={transaction} accounts={accounts} onSaved={onSaved} />
      </SheetBody>
      <SheetFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </SheetFooter>
    </Sheet>
  );
}

function AccountForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  useEscapeToClose(onClose);
  const baseCurrency = useBaseCurrency();
  const [form, setForm] = useState({
    name: '',
    type: 'bank' as Account['type'],
    bankName: '',
    accountNumberLast4: '',
    openingBalance: '',
    currency: baseCurrency,
  });
  const [terms, setTerms] = useState(emptyTerms);

  const create = useMutation({
    mutationFn: () =>
      api.post('/banking/accounts', {
        name: form.name,
        type: form.type,
        bankName: form.bankName || undefined,
        // Only the tail is sent. Fint never initiates a payment, so a full
        // account number is data that can only be lost.
        accountNumberLast4: form.accountNumberLast4.replace(/\D/g, '').slice(-4) || undefined,
        openingBalance: form.openingBalance || undefined,
        currency: form.currency !== baseCurrency ? form.currency : undefined,
        ...termsBody(form.type, terms),
      }),
    onSuccess: onCreated,
  });

  const error = create.error instanceof ApiError ? create.error : undefined;

  return (
    <Sheet label="Add account" onClose={onClose}>
      <SheetHeader title="Add account" onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <SheetBody>
          <Input
            label="Account name"
            required
            autoFocus
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={error?.fieldError('name')}
          />
          <AccountTypeSelect id="account-type" value={form.type} onChange={(type) => setForm((f) => ({ ...f, type }))} />
          <Input
            label={isInvestment(form.type) ? 'Institution' : 'Bank name'}
            value={form.bankName}
            onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))}
          />
          <Input
            label={isInvestment(form.type) ? 'Last 4 of the deposit / folio number' : 'Last 4 digits'}
            value={form.accountNumberLast4}
            onChange={(e) => setForm((f) => ({ ...f, accountNumberLast4: e.target.value }))}
            maxLength={4}
            hint="Only the last four are stored - enough to tell two accounts apart."
          />
          <InvestmentTermsFields type={form.type} terms={terms} onChange={setTerms} error={error} />
          <Input
            label="Opening balance"
            inputMode="decimal"
            value={form.openingBalance}
            onChange={(e) => setForm((f) => ({ ...f, openingBalance: e.target.value }))}
            hint={
              isInvestment(form.type)
                ? 'Leave at zero if the money came from an account here: record that as a transfer and the balance follows. Only an investment made before you started using Fint needs an opening balance.'
                : 'What was in it when you started tracking it here.'
            }
          />
          <CurrencySelect
            id="account-currency"
            value={form.currency}
            onChange={(code) => setForm((f) => ({ ...f, currency: code }))}
            base={baseCurrency}
            hint={
              form.currency === baseCurrency
                ? undefined
                : `A ${form.currency} account holds ${form.currency}. Its balance is shown in ${form.currency} and is listed beside the ${baseCurrency} cash figure, not added to it.`
            }
          />

          {error && !error.errors?.length ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={create.isPending}>Add account</Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

/**
 * Editing an account that exists - above all its OPENING BALANCE.
 *
 * ── WHY THIS DID NOT EXIST, AND WHAT IT COST ──
 * The opening balance could be set only on the "Add account" form. An account
 * made in a hurry with the default of zero then showed every balance a lakh
 * short, for ever, with the API's PATCH sitting unused. The first person to
 * reconcile a real statement against Fint hit it within the hour: the agent
 * told them to "set the opening balance to ₹1,00,531.73 dated 1 Apr" and there
 * was nowhere to do it.
 *
 * The balance on the card is opening balance + every transaction (see the
 * schema note), so changing this moves every balance shown from that date
 * onwards - which is what "opening balance" means, and the hint says so.
 */
function EditAccountForm({
  account,
  onClose,
  onSaved,
}: {
  account: Account;
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const [form, setForm] = useState({
    name: account.name,
    type: account.type,
    bankName: account.bankName ?? '',
    accountNumberLast4: account.accountNumberLast4 ?? '',
    openingBalance: account.openingBalance ?? '0',
    openingBalanceDate: account.openingBalanceDate ?? '',
    isPrimary: account.isPrimary,
  });
  const [terms, setTerms] = useState(() => termsFrom(account));

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/banking/accounts/${account.id}`, {
        name: form.name,
        type: form.type,
        bankName: form.bankName || undefined,
        accountNumberLast4: form.accountNumberLast4.replace(/\D/g, '').slice(-4) || undefined,
        openingBalance: form.openingBalance || '0',
        ...(form.openingBalanceDate ? { openingBalanceDate: form.openingBalanceDate } : {}),
        // Only sent when turning it ON: the server clears the others' flag
        // in the same transaction, and there is no "no primary" state.
        ...(form.isPrimary && !account.isPrimary ? { isPrimary: true } : {}),
        ...termsBody(form.type, terms),
      }),
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : undefined;

  return (
    <Sheet label={`Edit ${account.name}`} onClose={onClose}>
      <SheetHeader title="Edit account" onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          <Input
            label="Account name"
            required
            autoFocus
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={error?.fieldError('name')}
          />
          <AccountTypeSelect id="edit-account-type" value={form.type} onChange={(type) => setForm((f) => ({ ...f, type }))} />
          <Input
            label={isInvestment(form.type) ? 'Institution' : 'Bank name'}
            value={form.bankName}
            onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))}
          />
          <Input
            label={isInvestment(form.type) ? 'Last 4 of the deposit / folio number' : 'Last 4 digits'}
            value={form.accountNumberLast4}
            onChange={(e) => setForm((f) => ({ ...f, accountNumberLast4: e.target.value }))}
            maxLength={4}
          />
          <InvestmentTermsFields type={form.type} terms={terms} onChange={setTerms} error={error} />
          <Input
            label="Opening balance"
            inputMode="decimal"
            value={form.openingBalance}
            onChange={(e) => setForm((f) => ({ ...f, openingBalance: e.target.value }))}
            error={error?.fieldError('openingBalance')}
            hint="What was in the account the day you started tracking it here. Every balance from that day on is this plus the transactions."
          />
          <Input
            label="Opening balance date"
            type="date"
            value={form.openingBalanceDate}
            onChange={(e) => setForm((f) => ({ ...f, openingBalanceDate: e.target.value }))}
            error={error?.fieldError('openingBalanceDate')}
            hint="The statement date the opening balance is taken from - the first day Fint knows about."
          />
          {!account.isPrimary && !isInvestment(form.type) ? (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isPrimary}
                onChange={(e) => setForm((f) => ({ ...f, isPrimary: e.target.checked }))}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Make this the primary account</span>
                <span className="block text-muted-foreground">Quick add and new expenses pick it first.</span>
              </span>
            </label>
          ) : null}

          {error && !error.errors?.length ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={save.isPending}>Save changes</Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
